import setAuthToken from "./setAuthToken";
import { USER_TYPES } from "./userTypeConstants";
import { SET_IMPERSONATION_ACTIVE } from "../actions/types";
import { setCurrentUser } from "../actions/authActions";
import { fetchUserFeatures } from "../actions/featureAccessActions";

const SESSION_KEY = "impersonator_session";

/**
 * Returns the stored impersonator session object, or null if none exists.
 * Shape: { jwtToken, config, userType, userTimezone, userTimezoneId, page,
 *          businessId, tooltips, target: { name, email } }
 */
export const getImpersonatorSession = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/**
 * Swap into an impersonated user session.
 *
 * @param {object} tokenData    - { access_token, jwt_custom_claims } from the API
 * @param {object} targetUser   - The user row from the Users table (needs name + email
 *                                because jwt_custom_claims.user does NOT include email)
 * @param {function} dispatch   - Redux dispatch
 */
export const startImpersonation = (tokenData, targetUser, dispatch) => {
  const { access_token, jwt_custom_claims } = tokenData;

  // 1. Snapshot the current (super-admin) session so we can restore it on exit.
  //    Also store the target's display info since JWT claims don't include email.
  const snapshot = {
    jwtToken:      localStorage.getItem("jwtToken"),
    config:        localStorage.getItem("config"),
    userType:      localStorage.getItem("userType"),
    userTimezone:  localStorage.getItem("userTimezone"),
    userTimezoneId:localStorage.getItem("userTimezoneId"),
    page:          localStorage.getItem("page"),
    businessId:    localStorage.getItem("businessId"),
    tooltips:      localStorage.getItem("tooltips"),
    target: {
      name:  [targetUser.first_name, targetUser.last_name].filter(Boolean).join(" ").trim() || targetUser.email || "User",
      email: targetUser.email || "",
    },
  };
  localStorage.setItem(SESSION_KEY, JSON.stringify(snapshot));

  // 2. Write the impersonated session (mirrors authActions.js login flow).
  localStorage.setItem("jwtToken",       access_token);
  localStorage.setItem("config",         JSON.stringify(jwt_custom_claims));
  localStorage.setItem("userTimezone",   jwt_custom_claims.timezone   || "");
  localStorage.setItem("userTimezoneId", jwt_custom_claims.timezone_id || "");
  localStorage.setItem("page",           jwt_custom_claims.page        || "");

  // 3. Set userType to admin (all users returned by the subscribers list are admins).
  //    Use the pre-computed constant to avoid fragile CryptoJS coercion.
  localStorage.removeItem("userType");
  localStorage.setItem("userType", USER_TYPES.admin);

  // 4. Set businessId — remove old value first, then set from the target user's businesses.
  localStorage.removeItem("businessId");
  const bizIds = Object.values(jwt_custom_claims.businesses || {});
  if (bizIds.length > 0) {
    localStorage.setItem("businessId", bizIds[0]);
  }

  // 5. Update the axios Authorization header so all subsequent requests use the new token.
  setAuthToken(access_token);

  // 6. Update Redux auth state.
  dispatch(setCurrentUser(jwt_custom_claims.user));

  // 7. Signal the banner to appear.
  dispatch({ type: SET_IMPERSONATION_ACTIVE, payload: true });

  // 8. Refresh Redux featureAccess with the impersonated user's plan.
  dispatch(fetchUserFeatures());
};

/**
 * Exit the impersonation session and restore the original super-admin session.
 *
 * @param {function} dispatch   - Redux dispatch
 * @param {function|null} navigate - React Router navigate (pass null when calling
 *                                  from axios-instance.js; caller handles the redirect).
 */
export const exitImpersonation = (dispatch, navigate) => {
  const session = getImpersonatorSession();
  if (!session) return;

  // 1. Restore the original session keys.
  const set = (key, val) => {
    if (val !== null && val !== undefined) {
      localStorage.setItem(key, val);
    } else {
      localStorage.removeItem(key);
    }
  };

  set("jwtToken",        session.jwtToken);
  set("config",          session.config);
  set("userType",        session.userType);
  set("userTimezone",    session.userTimezone);
  set("userTimezoneId",  session.userTimezoneId);
  set("page",            session.page);
  set("businessId",      session.businessId);
  set("tooltips",        session.tooltips);

  // 2. Update the axios Authorization header back to the super-admin token.
  setAuthToken(session.jwtToken || false);

  // 3. Remove the impersonation snapshot.
  localStorage.removeItem(SESSION_KEY);

  // 4. Restore Redux auth state.
  try {
    const restoredConfig = JSON.parse(session.config || "{}");
    if (restoredConfig.user) {
      dispatch(setCurrentUser(restoredConfig.user));
    }
  } catch {
    // config was not parseable — state will be stale until next reload
  }

  // 5. Hide the banner.
  dispatch({ type: SET_IMPERSONATION_ACTIVE, payload: false });

  // 6. Restore featureAccess for the super-admin.
  dispatch(fetchUserFeatures());

  // 7. Navigate back to the super-admin users page (if navigate was provided).
  if (navigate) {
    navigate("/super-admin/subscriber");
  }
};
